import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api, pdfUrls } from '@/api';
import { useAuth } from '@/auth/useAuth';
import { Spinner } from '@/components/Spinner';
import { ReasonModal } from '@/components/ReasonModal';
import { SortHeader } from '@/components/SortHeader';
import { sortRows } from '@/lib/tableSort';
import { cn } from '@/lib/classNames';
import type { JacketSummary } from '@/types/api';

const PAGE_SIZE = 8;

function formatDate(iso: string): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${m}/${d}/${y}` : iso;
}
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export default function JacketListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 2;

  // Filters + page live in the URL so the view survives navigating to a record
  // and back (returns to the same page/filters, not page 1).
  const [searchParams, setSearchParams] = useSearchParams();
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const filter = searchParams.get('filter') ?? '';
  const over300k = searchParams.get('over300k') === '1';
  const page = Math.max(0, Number(searchParams.get('page') ?? '0') || 0);
  const sortKey = searchParams.get('sort') ?? '';
  const sortDir = searchParams.get('dir') === 'desc' ? 'desc' : 'asc';

  const setListParams = (changes: Record<string, string | number | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === undefined || v === '' || v === 0 || v === '0') next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: true });
  };

  const onSort = (field: string) =>
    setListParams(sortKey === field
      ? { dir: sortDir === 'asc' ? 'desc' : 'asc', page: 0 }
      : { sort: field, dir: 'asc', page: 0 });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['jackets', { from, to, filter, over300k }],
    queryFn: () => api.listJackets({ from, to, filter, over300k }),
  });

  const voidMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => api.voidJacket(id, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jackets'] }),
  });

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const val = (r: JacketSummary): string | number => {
      switch (sortKey) {
        case 'fileNumber': return r.fileNumber;
        case 'jacketNumber': return r.jacketNumber;
        case 'effectiveDate': return r.effectiveDate;
        case 'documentType': return r.documentType;
        case 'liabilityAmount': return r.liabilityAmount;
        case 'agentName': return r.agentName ?? '';
        default: return '';
      }
    };
    return sortRows(rows, sortDir, val);
  }, [rows, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = useMemo(
    () => sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [sorted, safePage],
  );

  const reset = () => setSearchParams(new URLSearchParams(), { replace: true });
  const colCount = isAdmin ? 8 : 7;

  const [printTarget, setPrintTarget] = useState<JacketSummary | null>(null);
  const [printBusy, setPrintBusy] = useState(false);
  const [voidTarget, setVoidTarget] = useState<JacketSummary | null>(null);

  const onVoidSubmit = (reason: string) => {
    if (!voidTarget) return;
    voidMutation.mutate({ id: voidTarget.id, reason });
    setVoidTarget(null);
  };

  // Log the reason, then open the generated jacket PDF in a new tab.
  const onPrint = async (reason: string) => {
    if (!printTarget) return;
    setPrintBusy(true);
    try {
      const res = await api.printDoc(printTarget.id, 'Jacket', 0, reason);
      if (res.err === '1' || res.err === 1) {
        window.open(pdfUrls.jacket(printTarget.id), '_blank', 'noopener');
        setPrintTarget(null);
      } else {
        window.alert(typeof res.message === 'string' ? res.message : 'Could not log the reason.');
      }
    } finally {
      setPrintBusy(false);
    }
  };

  return (
    <div className="ast-cpl">
      <div className="ast-page-head">
        <h2 className="serif ast-page-title">Jackets</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => navigate('/jackets/new?upload=1')}>
            <i className="bi bi-cloud-arrow-up me-1" />
            New from Upload
          </button>
          <button className="btn btn-ast-blue" onClick={() => navigate('/jackets/new')}>
            <i className="bi bi-plus-lg me-1" />
            New Jacket
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="ast-filters">
        <div className="ast-filter-field">
          <label className="ast-field-label" htmlFor="jk-from">From</label>
          <input id="jk-from" type="date" className="form-control form-control-sm"
            value={from} onChange={(e) => setListParams({ from: e.target.value, page: 0 })} />
        </div>
        <div className="ast-filter-field">
          <label className="ast-field-label" htmlFor="jk-to">To</label>
          <input id="jk-to" type="date" className="form-control form-control-sm"
            value={to} onChange={(e) => setListParams({ to: e.target.value, page: 0 })} />
        </div>
        <div className="ast-filter-field ast-filter-grow">
          <label className="ast-field-label" htmlFor="jk-filter">
            {isAdmin ? 'Agent Name or File #' : 'File #'}
          </label>
          <div className="ast-input-icon">
            <i className="bi bi-funnel" />
            <input id="jk-filter" className="form-control form-control-sm"
              placeholder={isAdmin ? 'Agent name or file #…' : 'File #…'}
              value={filter} onChange={(e) => setListParams({ filter: e.target.value, page: 0 })} />
          </div>
        </div>
        {isAdmin && (
          <div className="form-check ast-over300k">
            <input className="form-check-input" type="checkbox" id="jk-over300k"
              checked={over300k} onChange={(e) => setListParams({ over300k: e.target.checked ? '1' : '', page: 0 })} />
            <label className="form-check-label" htmlFor="jk-over300k">&gt; $300,000</label>
          </div>
        )}
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => {
            if (!from || !to) { window.alert('Please choose a From and To date for the report.'); return; }
            window.open(pdfUrls.remit(from, to), '_blank', 'noopener');
          }}
        >
          Report
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={reset}>Reset</button>
      </div>

      {/* Table */}
      <div className="ast-card">
        <table className="table table-hover align-middle ast-table mb-0">
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <SortHeader label="File Number" field="fileNumber" activeField={sortKey} dir={sortDir} onSort={onSort} />
              <SortHeader label="Jacket Number" field="jacketNumber" activeField={sortKey} dir={sortDir} onSort={onSort} />
              <SortHeader label="Effective Date" field="effectiveDate" activeField={sortKey} dir={sortDir} onSort={onSort} />
              <SortHeader label="Document Type" field="documentType" activeField={sortKey} dir={sortDir} onSort={onSort} />
              <SortHeader label="Liability Amount" field="liabilityAmount" activeField={sortKey} dir={sortDir} onSort={onSort} className="text-end" />
              {isAdmin && <SortHeader label="Agent" field="agentName" activeField={sortKey} dir={sortDir} onSort={onSort} />}
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={colCount + 1} className="text-center py-4"><Spinner /></td></tr>
            )}
            {!isLoading && pageRows.length === 0 && (
              <tr><td colSpan={colCount + 1} className="text-center text-muted py-4">No jackets found.</td></tr>
            )}
            {!isLoading && pageRows.map((r) => {
              return (
                <tr key={r.id} className={cn(r.void && 'ast-row-void')}>
                  <td className="text-center">
                    <i className={cn('bi', r.void ? 'bi-x-circle text-danger' : 'bi-folder2-open text-ast')} />
                  </td>
                  <td>
                    <button className="ast-link" onClick={() => navigate(`/jackets/${r.id}/edit`)}>
                      {r.fileNumber}
                    </button>
                    {r.void && <span className="badge ast-badge-void ms-2">Voided</span>}
                  </td>
                  <td>{r.jacketNumber}</td>
                  <td>{formatDate(r.effectiveDate)}</td>
                  <td>{r.documentType}</td>
                  <td className="text-end">{money.format(r.liabilityAmount)}</td>
                  {isAdmin && <td>{r.agentName ?? '—'}</td>}
                  <td className="text-end ast-actions">
                    <button className="btn btn-sm btn-outline-secondary" title="View / Print"
                      onClick={() => setPrintTarget(r)}>
                      <i className="bi bi-printer" />
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" title="Edit"
                      onClick={() => navigate(`/jackets/${r.id}/edit`)}>
                      <i className="bi bi-pencil" />
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" title="Add Image"
                      onClick={() => navigate(`/jackets/${r.id}/edit?upload=1`)}>
                      <i className="bi bi-cloud-arrow-up" />
                    </button>
                    <button className="btn btn-sm btn-outline-danger" title="Void"
                      disabled={r.void} onClick={() => setVoidTarget(r)}>
                      <i className="bi bi-x-lg" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="ast-pager">
        <button className="btn btn-sm btn-outline-secondary" disabled={safePage <= 0}
          onClick={() => setListParams({ page: safePage - 1 })} title="Previous page">
          <i className="bi bi-chevron-left" />
        </button>
        <span className="ast-pager-info">
          Showing {rows.length === 0 ? 0 : safePage * PAGE_SIZE + 1}–
          {Math.min(rows.length, (safePage + 1) * PAGE_SIZE)} of {rows.length}
        </span>
        <button className="btn btn-sm btn-outline-secondary" disabled={safePage >= pageCount - 1}
          onClick={() => setListParams({ page: safePage + 1 })} title="Next page">
          <i className="bi bi-chevron-right" />
        </button>
      </div>

      {printTarget && (
        <ReasonModal
          title="Reason for View / Print"
          label={printTarget.fileNumber}
          submitLabel="Open"
          busyLabel="Opening…"
          busy={printBusy}
          onCancel={() => setPrintTarget(null)}
          onSubmit={onPrint}
        />
      )}
      {voidTarget && (
        <ReasonModal
          title="Void Jacket"
          label={voidTarget.fileNumber}
          placeholder="Reason for voiding…"
          submitLabel="Void"
          busyLabel="Voiding…"
          busy={voidMutation.isPending}
          onCancel={() => setVoidTarget(null)}
          onSubmit={onVoidSubmit}
        />
      )}
    </div>
  );
}
